import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/client';
import type { Paginated } from '@/api/types';

import type {
  InboxItem,
  NotificationChannel,
  NotificationEvent,
  NotificationLog,
  NotificationStatus,
  NotificationTemplate,
  UnreadCount,
} from './types';

export const notificationKeys = {
  all: ['notifications'] as const,
  inbox: ['notifications', 'inbox'] as const,
  unread: ['notifications', 'inbox', 'unread'] as const,
  templates: ['notifications', 'templates'] as const,
  logAll: ['notifications', 'log'] as const,
  log: (filters: object) => ['notifications', 'log', filters] as const,
};

/** Nombre de notifications internes non lues (rafraîchi toutes les 60 s). */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unread,
    queryFn: () => apiFetch<UnreadCount>('/notifications/inbox/unread-count'),
    refetchInterval: 60_000,
  });
}

export function useInbox(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.inbox,
    queryFn: () =>
      apiFetch<Paginated<InboxItem>>('/notifications/inbox', { query: { page_size: 15 } }),
    enabled,
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: notificationKeys.templates,
    queryFn: () =>
      apiFetch<Paginated<NotificationTemplate>>('/notifications/templates', {
        query: { page_size: 100 },
      }),
  });
}

export function useNotificationLog(filters: {
  status?: NotificationStatus | null;
  channel?: NotificationChannel | null;
  event?: NotificationEvent | null;
  page?: number;
}) {
  return useQuery({
    queryKey: notificationKeys.log(filters),
    queryFn: () =>
      apiFetch<Paginated<NotificationLog>>('/notifications/log', {
        query: {
          status: filters.status ?? undefined,
          channel: filters.channel ?? undefined,
          event: filters.event ?? undefined,
          page: filters.page ?? 1,
          page_size: 50,
        },
      }),
  });
}

export const EVENTS: { value: NotificationEvent; label: string }[] = [
  { value: 'RDV_CONFIRME', label: 'Confirmation de rendez-vous' },
  { value: 'RDV_RAPPEL', label: 'Rappel de rendez-vous (veille)' },
  { value: 'RESULTAT_DISPONIBLE', label: 'Disponibilité de résultats' },
  { value: 'PAIEMENT_RECU', label: 'Paiement reçu' },
  { value: 'INFO_ADMIN', label: 'Information administrative' },
  { value: 'RESULTAT_CRITIQUE', label: 'Alerte interne : résultat critique' },
  { value: 'ABSENCE_DEMANDEE', label: 'Alerte interne : demande d’absence' },
  { value: 'ALERTE_STOCK', label: 'Alerte interne : stocks' },
];

export const CHANNELS: { value: NotificationChannel; label: string }[] = [
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'INTERNE', label: 'Dans l’application' },
];

export const STATUSES: { value: NotificationStatus; label: string }[] = [
  { value: 'EN_ATTENTE', label: 'En attente' },
  { value: 'ENVOYEE', label: 'Envoyée' },
  { value: 'ECHEC', label: 'Échec' },
  { value: 'NON_ENVOYEE', label: 'Non envoyée' },
];

export const STATUS_COLORS: Record<NotificationStatus, string> = {
  EN_ATTENTE: 'blue',
  ENVOYEE: 'teal',
  ECHEC: 'red',
  NON_ENVOYEE: 'gray',
};

const CLINIC = [
  { name: 'clinique', hint: 'nom du site' },
  { name: 'telephone_clinique', hint: 'téléphone de la clinique' },
];
const PATIENT = [
  { name: 'prenom', hint: 'prénom du patient' },
  { name: 'nom', hint: 'nom du patient' },
  ...CLINIC,
];
const APPOINTMENT = [
  { name: 'date', hint: 'JJ/MM/AAAA' },
  { name: 'heure', hint: 'HH:MM' },
  { name: 'service', hint: 'service du rendez-vous' },
];

/** Variables remplies par le serveur pour chaque événement (apps/notifications/services.py). */
export const TEMPLATE_VARIABLES: Record<NotificationEvent, { name: string; hint: string }[]> = {
  RDV_CONFIRME: [...PATIENT, ...APPOINTMENT],
  RDV_RAPPEL: [...PATIENT, ...APPOINTMENT],
  RESULTAT_DISPONIBLE: PATIENT,
  PAIEMENT_RECU: [
    ...PATIENT,
    { name: 'montant', hint: 'montant payé' },
    { name: 'reference', hint: 'n° du reçu' },
  ],
  INFO_ADMIN: [...PATIENT, { name: 'message', hint: 'texte saisi par l’agent' }],
  RESULTAT_CRITIQUE: [
    ...CLINIC,
    { name: 'analyse', hint: 'nom de l’analyse' },
    { name: 'patient', hint: 'nom du patient' },
    { name: 'numero_patient', hint: 'n° de dossier' },
  ],
  ABSENCE_DEMANDEE: [
    ...CLINIC,
    { name: 'agent', hint: 'nom de l’agent' },
    { name: 'type', hint: 'type d’absence' },
    { name: 'debut', hint: 'date de début' },
    { name: 'fin', hint: 'date de fin' },
  ],
  ALERTE_STOCK: [
    ...CLINIC,
    { name: 'sous_seuil', hint: 'produits sous le seuil' },
    { name: 'bientot_perimes', hint: 'lots bientôt périmés' },
    { name: 'perimes', hint: 'lots périmés' },
  ],
};

/** Variables utilisées dans un texte mais inconnues pour l'événement (restent vides à l'envoi). */
export function unknownVariables(event: NotificationEvent, ...texts: string[]): string[] {
  const known = new Set(TEMPLATE_VARIABLES[event].map((v) => v.name));
  const found = new Set<string>();
  for (const text of texts) {
    for (const match of text.matchAll(/\{([a-z_]+)\}/g)) {
      if (!known.has(match[1]!)) found.add(match[1]!);
    }
  }
  return [...found];
}
